import React from 'react';

function TestSetup({
  st, setSt, getDefs, getPF, OW, darkMode, DM, ss, lb, IC, SYS_LOCS, isReadOnly,
  allLocs,
  lockedLoc,
  bTester, setBTester,
  batchHomeLoc, setBatchHomeLoc,
  bMode, setBMode,
  bFwVersion, setBFwVersion,
  setupMode, setSetupMode,
  browseSearch, setBrowseSearch,
  browseSel, setBrowseSel,
  serialInput, setSerialInput,
  locOverrides, setLocOverrides,
  editLocId, setEditLocId,
  parsedSerials, foundDevs, notFoundSerials,
  scanFwMode, scanFwMixed,
  startBatch,
  applyScanner,
  pencilIC,
  PhoneScannerScreen,
  onBack,
  FilterSelect,
}) {
  const [showQrScan, setShowQrScan] = React.useState(false);
  const selDevsFw = browseSel.map(id => (st.devices || []).find(d => d.id === id)).filter(Boolean);
  const bFwVals = selDevsFw.map(d => d.firmware || '').filter(Boolean);
  const bFwMode = bFwVals.length ? [...bFwVals].sort((a, b) => bFwVals.filter(v => v === b).length - bFwVals.filter(v => v === a).length)[0] : null;
  const bFwMixed = new Set(bFwVals).size > 1;

  const canStart = !isReadOnly && !!batchHomeLoc;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div className="card" style={{ padding: 24 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <button
            onClick={onBack}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 6px', borderRadius: 6, display: 'flex', alignItems: 'center' }}
            title="Back to overview"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Start Test Session</div>
        </div>

        {/* Tester */}
        <div style={{ marginBottom: 16 }}>
          <label style={lb}>Tester</label>
          <input className="input" value={bTester} onChange={e => setBTester(e.target.value)} placeholder="Enter tester name..." style={{ width: '100%', fontSize: 13 }} />
        </div>

        {/* Location */}
        <div style={{ marginBottom: 16 }}>
          <label style={lb}>Location</label>
          {lockedLoc ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--surface-inset)', fontSize: 13 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success-icon)', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{lockedLoc}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>Set from overview</span>
            </div>
          ) : (
            <>
              {FilterSelect ? (
                <FilterSelect
                  value={batchHomeLoc}
                  onChange={setBatchHomeLoc}
                  darkMode={darkMode} OW={OW} DM={DM}
                  options={[
                    { value: '', label: 'Select a location...' },
                    { value: 'In Stock', label: 'In Stock' },
                    ...allLocs.filter(l => !['In Testing', 'Quarantine'].includes(l)).map(l => ({ value: l, label: l })),
                  ]}
                />
              ) : (
                <select className="input" value={batchHomeLoc} onChange={e => setBatchHomeLoc(e.target.value)} style={{ width: '100%' }}>
                  <option value="">Select a location...</option>
                  <option value="In Stock">In Stock</option>
                  {allLocs.filter(l => !['In Testing', 'Quarantine'].includes(l)).map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              )}
              {!batchHomeLoc && <div style={{ marginTop: 4, fontSize: 11, color: 'var(--danger-text)' }}>A location is required to start testing.</div>}
            </>
          )}
        </div>

        {/* Test Mode */}
        <div style={{ marginBottom: 16 }}>
          <label style={lb}>Test Mode</label>
          <div className="tabs" style={{ marginBottom: 0 }}>
            {[['standard', 'Standard Test'], ['firmware', 'Firmware Test']].map(([id, l]) => (
              <button key={id} className={'tab' + (bMode === id ? ' tab-active' : '')} onClick={() => { setBMode(id); if (id === 'standard') setBFwVersion(''); }}>{l}</button>
            ))}
          </div>
        </div>

        {bMode === 'firmware' && (
          <div style={{ marginBottom: 16 }}>
            <label style={lb}>Firmware Version Being Tested</label>
            <input className="input" value={bFwVersion} onChange={e => setBFwVersion(e.target.value)} placeholder="e.g. 2.5.0" style={{ width: '100%', fontSize: 13, fontFamily: 'monospace' }} />
            <div style={{ marginTop: 6, fontSize: 11, padding: '6px 10px', borderRadius: 6, background: 'var(--surface-inset)', border: '1px solid var(--border-default)', color: 'var(--text-muted)' }}>
              Results will be stored separately and will <strong>not</strong> affect device health scores.
            </div>
          </div>
        )}

        {/* Sub-mode tabs */}
        <div className="tabs" style={{ marginBottom: 16 }}>
          {[['browse', 'Browse Stock'], ['scan', 'Scan Serials'], ['history', 'History']].map(([id, l]) => (
            <button key={id} className={'tab' + (setupMode === id ? ' tab-active' : '')} onClick={() => setSetupMode(id)}>{l}</button>
          ))}
        </div>

        {/* BROWSE */}
        {setupMode === 'browse' && (() => {
          const browsable = (st.devices || []).filter(d => d.location !== 'Quarantine' && d.status !== 'Quarantine');
          const bTerms = browseSearch.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
          const filtered = bTerms.length ? browsable.filter(d => bTerms.some(t => (d.serial || '').toLowerCase().includes(t) || (d.type || '').toLowerCase().includes(t) || (d.location || '').toLowerCase().includes(t))) : browsable;
          return (<>
            <div style={{ marginBottom: 10 }} className="input-with-icon">
              <svg className="input-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="input" value={browseSearch} onChange={e => setBrowseSearch(applyScanner(browseSearch, e.target.value, 'replace'))} placeholder="Search by serial or type..." style={{ width: '100%', fontSize: 13 }} />
              {browseSearch && <button onClick={() => setBrowseSearch('')} aria-label="Clear search" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex', alignItems: 'center' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{browseSel.length} selected</span>
              <button onClick={() => setBrowseSel(browseSel.length === filtered.length && filtered.length > 0 ? [] : filtered.map(d => d.id))} className="btn btn-ghost" style={{ fontSize: 11 }}>
                {browseSel.length === filtered.length && filtered.length > 0 ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, overflow: 'hidden', maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
              {filtered.length === 0
                ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No devices available</div>
                : filtered.map((d, i) => {
                  const isSel = browseSel.includes(d.id);
                  const fwMismatch = isSel && bFwMixed && d.firmware && d.firmware !== bFwMode;
                  return (
                    <div key={d.id}
                      onClick={() => setBrowseSel(s => isSel ? s.filter(x => x !== d.id) : [...s, d.id])}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: isSel ? 'var(--brand-tint)' : 'var(--surface-default)', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer', userSelect: 'none', transition: 'background 0.1s' }}
                      onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--surface-raised)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isSel ? 'var(--brand-tint)' : 'var(--surface-default)'; }}
                    >
                      {/* Checkbox */}
                      <div style={{ width: 16, height: 16, borderRadius: 4, border: isSel ? '2px solid var(--brand-default)' : '2px solid var(--border-hover)', background: isSel ? 'var(--brand-default)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {isSel && <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="2 6 5 9 10 3"/></svg>}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{d.serial}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.type}</span>
                      {fwMismatch && <span title={'FW ' + d.firmware + ' differs from majority (' + bFwMode + ')'} className="pill pill-warning" style={{ fontSize: 10 }}>FW {d.firmware}</span>}
                      <span style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'var(--surface-inset)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}>{d.location}</span>
                    </div>
                  );
                })}
            </div>
            <button onClick={startBatch} disabled={!browseSel.length || !canStart} className="btn btn-primary" style={{ width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 600, opacity: (!browseSel.length || !canStart) ? 0.5 : 1 }}>
              {isReadOnly ? 'Read Only — Cannot Start Test' : !batchHomeLoc ? 'Select a location to continue' : 'Start Testing · ' + browseSel.length + ' device' + (browseSel.length === 1 ? '' : 's')}
            </button>
          </>);
        })()}

        {/* SCAN */}
        {setupMode === 'scan' && (<>
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ ...lb, marginBottom: 0 }}>Scan Device Serials <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(scan or paste comma-separated)</span></label>
              {PhoneScannerScreen && (
                <button
                  onClick={() => setShowQrScan(v => !v)}
                  className="btn btn-ghost"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '4px 10px', flexShrink: 0 }}
                  title={showQrScan ? 'Close camera' : 'Scan QR code with camera'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  {showQrScan ? 'Close' : 'Scan QR'}
                </button>
              )}
            </div>
            {showQrScan && PhoneScannerScreen && (
              <div style={{ marginBottom: 10, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-default)', background: 'var(--surface-inset)', maxHeight: 200 }}>
                <PhoneScannerScreen compact onScan={raw => {
                  setSerialInput(prev => applyScanner(prev, raw, 'append'));
                }} />
              </div>
            )}
            <textarea
              className="input"
              value={serialInput}
              onChange={e => setSerialInput(e.target.value.replace(/https?:\/\/\S*?\/sn\/([^/?#\s,]+)\S*/gi, '$1'))}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setSerialInput(v => { const t = v.trimEnd(); if (!t) return v; return t.endsWith(',') ? t : t + ', '; });
                }
              }}
              placeholder="Scan serials here..."
              rows={6}
              style={{ width: '100%', fontFamily: 'monospace', letterSpacing: 0.5, resize: 'vertical', fontSize: 13, minHeight: 120 }}
            />
          </div>
          {parsedSerials.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{foundDevs.length} found · {notFoundSerials.length} not found</div>
              <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, overflow: 'hidden' }}>
                {foundDevs.map((d, i) => {
                  const loc = locOverrides[d.id] || d.location;
                  const isEditing = editLocId === d.id;
                  const fwMismatch = scanFwMixed && d.firmware && d.firmware !== scanFwMode;
                  return (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'var(--surface-default)', borderBottom: i < foundDevs.length - 1 || notFoundSerials.length > 0 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13, color: 'var(--text-primary)', minWidth: 110 }}>{d.serial}</span>
                      <span className="pill pill-info" style={{ fontSize: 11 }}>{d.type}</span>
                      {fwMismatch && <span title={'FW ' + d.firmware + ' differs from majority (' + scanFwMode + ')'} className="pill pill-warning" style={{ fontSize: 10 }}>FW {d.firmware}</span>}
                      <span style={{ flex: 1 }} />
                      {isEditing ? (
                        <select className="input" value={loc} onChange={e => { setLocOverrides(o => ({ ...o, [d.id]: e.target.value })); setEditLocId(null); }} style={{ fontSize: 12, padding: '3px 8px' }} autoFocus onBlur={() => setEditLocId(null)}>
                          {[...SYS_LOCS, ...allLocs.filter(l => !SYS_LOCS.includes(l))].map(l => <option key={l}>{l}</option>)}
                        </select>
                      ) : (
                        <button onClick={() => setEditLocId(d.id)} title="Override location" className="btn btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                          {pencilIC} {loc}
                        </button>
                      )}
                    </div>
                  );
                })}
                {notFoundSerials.map((s, i) => (
                  <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'var(--danger-fill)', borderBottom: i < notFoundSerials.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                    <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13, color: 'var(--danger-text)' }}>{s}</span>
                    <span style={{ fontSize: 11, color: 'var(--danger-text)' }}>✗ Not found in stock</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button onClick={startBatch} disabled={!foundDevs.length || !canStart} className="btn btn-primary" style={{ width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 600, opacity: (!foundDevs.length || !canStart) ? 0.5 : 1 }}>
            {isReadOnly ? 'Read Only — Cannot Start Test' : !batchHomeLoc ? 'Select a location to continue' : 'Start Testing · ' + foundDevs.length + ' device' + (foundDevs.length === 1 ? '' : 's')}
          </button>
        </>)}

        {/* HISTORY */}
        {setupMode === 'history' && (() => {
          const recent = [...(st.sessions || [])].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
          if (recent.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No test history yet. Complete a test session to see it here.</div>;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recent.map(s => {
                const sessionDevIds = (st.testResults || []).filter(r => r.sessionId === s.id).map(r => r.deviceId);
                const availableDevs = sessionDevIds.map(id => (st.devices || []).find(d => d.id === id)).filter(Boolean);
                const removedCount = sessionDevIds.length - availableDevs.length;
                return (
                  <div key={s.id} className="card" style={{ padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{new Date(s.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.tester}{s.homeLoc ? ' · ' + s.homeLoc : ''}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span className="pill pill-success" style={{ fontSize: 11 }}>{s.passCount}P</span>
                        {s.failCount > 0 && <span className="pill pill-danger" style={{ fontSize: 11 }}>{s.failCount}F</span>}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.deviceCount} device{s.deviceCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {availableDevs.map(d => <span key={d.id} className="pill pill-neutral" style={{ fontSize: 11 }}>{d.serial}</span>)}
                      {removedCount > 0 && <span className="pill pill-neutral" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{removedCount} removed</span>}
                    </div>
                    <button disabled={availableDevs.length === 0} onClick={() => { setBrowseSel(availableDevs.map(d => d.id)); setSetupMode('browse'); }} className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 14px', opacity: availableDevs.length === 0 ? 0.5 : 1 }}>
                      Reuse {availableDevs.length} Device{availableDevs.length !== 1 ? 's' : ''}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })()}

      </div>
    </div>
  );
}

export default TestSetup;
